import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const money = (n) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD" });
const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
// Row-level transaction date — compact yy-mm-dd (2026-08-20).
const fmtDateShort = (iso) => (iso ? `${iso.slice(2, 4)}-${iso.slice(5, 7)}-${iso.slice(8, 10)}` : "");

const fyMonths = (fromIso) => {
  if (!fromIso) return [];
  const start = new Date(fromIso + "T00:00:00");
  const out = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    out.push({ ym, label });
  }
  return out;
};

/* -- Coloured pay-status pill for a transaction row -- */
function PayLit({ txn }) {
  if (txn.pay_tp === 0) {
    return <span className="pay-lit-pending">{txn.pay_lit || "PENDING"}</span>;
  }
  if (txn.pay_tp === 1) {
    return <span className="pay-lit-approved">{txn.pay_lit || "APPROVED"}</span>;
  }
  if (txn.pay_tp === 2) {
    return <span className="pay-lit-rejected">{txn.pay_lit || "REJECTED"}</span>;
  }
  return <span>{txn.pay_lit || txn.pay_tp}</span>;
}

export default function TransactionsTable({
  calSeq, selectedTxn, onSelect, fyFrom,
  onExportCsv, onEdit, onDelete, refreshKey,
  fys = [], setCalSeq, loadingFys = false, selectedFy, onNewTxn,
  view, onChangeView,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [refs, setRefs] = useState(null);
  const [accessError, setAccessError] = useState(null);
  const [filters, setFilters] = useState({ pye_id: "", codes_id: "", month: "", pay_tp: "", txn_chk: "" });
  const limit = 24;

  // Load reference data once on mount (api is a stable module import — safe to omit from deps)
  useEffect(() => {
    let ignore = false;
    api.refs().then((r) => { if (!ignore) setRefs(r); }).catch(() => {});
    return () => { ignore = true; };
  }, []);

  // Reset pagination when the calendar year or any filter changes.
  // Depend on primitive filter fields so identity-only changes don't retrigger.
  useEffect(() => { setOffset(0); }, [
    calSeq,
    filters.pye_id, filters.codes_id, filters.month, filters.pay_tp, filters.txn_chk,
  ]);

  useEffect(() => {
    if (!calSeq) return undefined;
    let ignore = false;
    setLoading(true);
    // Translate "" → omitted for the network call; pay_tp may be 0 which is truthy
    const flt = {
      pye_id: filters.pye_id || undefined,
      codes_id: filters.codes_id || undefined,
      month: filters.month || undefined,
      txn_chk: filters.txn_chk || undefined,
      pay_tp: filters.pay_tp === "" ? undefined : Number(filters.pay_tp),
    };
    api.transactions(calSeq, offset, limit, flt)
      .then((r) => { if (!ignore) { setData(r); setAccessError(null); } })
      .catch((err) => {
        if (ignore) return;
        setData({ items: [], total: 0 });
        setAccessError(
          err?.response?.status === 403
            ? (err.response.data?.detail || "You don't have a Transactions grant.")
            : null
        );
      })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [
    calSeq, offset, refreshKey, limit,
    filters.pye_id, filters.codes_id, filters.month, filters.pay_tp, filters.txn_chk,
  ]);

  // fyMonths is a module-level pure function — stable across renders
  const months = useMemo(() => fyMonths(fyFrom), [fyFrom]);

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + items.length, total);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const anyFilter = filters.pye_id || filters.codes_id || filters.month || filters.pay_tp !== "" || filters.txn_chk;
  const clearFilters = () => setFilters({ pye_id: "", codes_id: "", month: "", pay_tp: "", txn_chk: "" });

  return (
    <section>
      <p className="section-title">Transactions</p>
      <div className="card list-card txn-card" data-testid="txn-card">
        <div className="list-header txn-header">
          <div className="list-header-left txn-header-left">
            {onChangeView && (
              <>
                <label className="budget-label" htmlFor="txn-view-select">View</label>
                <select
                  id="txn-view-select"
                  className="fy-select"
                  data-testid="txn-view-select"
                  value={view || "all"}
                  onChange={(e) => onChangeView(e.target.value)}
                  title="Transactions view"
                  style={{ marginRight: 12 }}
                >
                  <option value="all">ALL</option>
                  <option value="pending">PENDING</option>
                </select>
              </>
            )}
            {loadingFys ? (
              <div className="loading-row"><span className="spinner" /> Loading…</div>
            ) : (
              <>
                <label className="budget-label" htmlFor="txn-fy-select">Year</label>
                <select
                  id="txn-fy-select"
                  className="fy-select"
                  data-testid="fy-select"
                  value={calSeq}
                  onChange={(e) => setCalSeq && setCalSeq(e.target.value)}
                  title="Fiscal year"
                >
                  {fys.map((f) => (
                    <option key={f.cal_seq} value={f.cal_seq}>{f.cal_year}</option>
                  ))}
                </select>
                {selectedFy && (
                  <span className="fy-range muted" data-testid="fy-range">
                    {fmtDate(selectedFy.cal_frm)} – {fmtDate(selectedFy.cal_to)}
                  </span>
                )}
                <span className="count" data-testid="txn-count">
                  {total === 0 ? "0 results" : `${total} txn${total === 1 ? "" : "s"}`}
                </span>
                {anyFilter && (
                  <button className="btn-ghost btn-sm" onClick={clearFilters} data-testid="flt-clear">
                    Clear filters
                  </button>
                )}
              </>
            )}
          </div>
          <div className="list-header-right txn-header-right">
            {onNewTxn && (
              <button
                className="btn-navy"
                data-testid="new-txn-btn"
                onClick={onNewTxn}
                title="Create a new transaction"
              >+ New transaction</button>
            )}
            <button
              className="btn-navy"
              data-testid="export-csv-btn"
              onClick={onExportCsv}
              title="Download this fiscal year's transactions as CSV"
              style={{ visibility: onExportCsv ? "visible" : "hidden" }}
            >↓ Export CSV</button>
          </div>
        </div>

        <div className="list-scroll txn-scroll" data-testid="txn-scroll">
          {(() => {
            if (loading && !items.length) {
              return <div className="loading-row"><span className="spinner" /> Loading…</div>;
            }
            if (accessError) {
              return (
                <div className="empty" data-testid="txn-access-error">
                  <span className="big" aria-hidden>⚠</span>
                  {accessError}
                </div>
              );
            }
            if (items.length === 0) {
              return (
                <div className="empty">
                  <span className="big" aria-hidden>∅</span>
                  No transactions match the current filters.
                </div>
              );
            }
            return (
            <table className="list-table list-table-compact txn-table txn-table-compact">
              <thead>
                <tr className="list-head-labels txn-head-labels">
                  <th>Pay Date</th>
                  <th>Pay Type</th>
                  <th>Check #</th>
                  <th>Payee</th>
                  <th>Code</th>
                  <th>Note</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                </tr>
                <tr className="list-head-filters txn-head-filters">
                  <th>
                    <select
                      data-testid="flt-month"
                      value={filters.month}
                      onChange={(e) => setFilter("month", e.target.value)}
                    >
                      <option value="">All months</option>
                      {months.map((m) => (
                        <option key={m.ym} value={m.ym}>{m.label}</option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select
                      data-testid="flt-paytp"
                      value={filters.pay_tp}
                      onChange={(e) => setFilter("pay_tp", e.target.value)}
                    >
                      <option value="">All types</option>
                      {refs?.payments.map((p) => (
                        <option key={p.id} value={p.id}>{p.id} · {p.label}</option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <input
                      data-testid="flt-chk"
                      type="text"
                      placeholder="Check #"
                      maxLength={5}
                      value={filters.txn_chk}
                      onChange={(e) => setFilter("txn_chk", e.target.value)}
                      style={{ width: "100%", padding: "4px 8px", fontSize: 13,
                               border: "1px solid var(--nyse-line)", borderRadius: 4 }}
                    />
                  </th>
                  <th>
                    <select
                      data-testid="flt-pye"
                      value={filters.pye_id}
                      onChange={(e) => setFilter("pye_id", e.target.value)}
                    >
                      <option value="">All payees</option>
                      {refs?.payees.map((p) => (
                        <option key={p.id} value={p.id}>{p.label} ({p.id})</option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select
                      data-testid="flt-code"
                      value={filters.codes_id}
                      onChange={(e) => setFilter("codes_id", e.target.value)}
                    >
                      <option value="">All codes</option>
                      {refs?.codes.map((c) => (
                        <option key={c.id} value={c.id}>{c.id} · {c.label}</option>
                      ))}
                    </select>
                  </th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr
                    key={t.txn_seq}
                    className={selectedTxn?.txn_seq === t.txn_seq ? "selected" : ""}
                    onClick={() => onSelect(t)}
                    data-testid={`txn-row-${t.txn_seq}`}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{fmtDateShort(t.pay_dt)}</td>
                    <td
                      className="pay-tp-cell"
                      data-testid={`pay-tp-${t.txn_seq}`}
                    >
                      <PayLit txn={t} />
                    </td>
                    <td className="muted">{t.txn_chk || "—"}</td>
                    <td>
                      {t.pye_nm || t.pye_id}{" "}
                      <span className="muted" style={{ fontSize: 11 }}>({t.pye_id})</span>
                    </td>
                    <td>
                      {t.codes_id}{" "}
                      <span className="muted" style={{ fontSize: 11 }}>
                        {t.codes_lit ? `· ${t.codes_lit}` : ""}
                      </span>
                    </td>
                    <td className="muted note-cell">{t.txn_note || "—"}</td>
                    <td className="amount">{money(t.txn_amt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            );
          })()}
        </div>

        {total > limit && (
          <div className="list-pager txn-pager" data-testid="txn-pager">
            <span className="muted" data-testid="txn-pager-info">
              Showing {showingFrom}–{showingTo} of {total}
            </span>
            <div className="txn-pager-btns">
              <button className="btn-ghost" data-testid="txn-prev"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}>‹ Prev</button>
              <button className="btn-ghost" data-testid="txn-next"
                disabled={offset + limit >= total}
                onClick={() => setOffset(offset + limit)}>Next ›</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
